import type { FaqItem } from '../types/help-center.types';

export const faqItems: FaqItem[] = [
  // ── General ────────────────────────────────────────────────────
  {
    id: 'gen-1',
    question: '¿Qué es Prixia?',
    answer:
      'Prixia es la plataforma de precios de tu empresa. Junta tres módulos: Cotiza (cotizaciones con modelos de precio, márgenes y aprobaciones), Compite (tus precios contra los de la competencia) y Comercia (el costo real de importar: flete, embarques y aranceles). Además tiene Datos Maestros, Integraciones y Mi empresa para la configuración.',
    category: 'general',
  },
  {
    id: 'gen-2',
    question: '¿Para quién está pensada?',
    answer:
      'Para empresas que cotizan con muchas variables, márgenes y aprobaciones, para comercios que necesitan seguir los precios de la competencia, y para importadores que quieren saber cuánto les cuesta de verdad poner un producto en su almacén. Cada empresa usa los módulos que tiene habilitados.',
    category: 'general',
  },
  {
    id: 'gen-3',
    question: '¿Otras empresas pueden ver mis datos?',
    answer:
      'No. Cada empresa tiene sus datos completamente aislados: usuarios, modelos, cotizaciones, productos, precios y datos maestros. Nadie de otra empresa ve tus precios, tus clientes ni tus cotizaciones.',
    category: 'general',
  },
  {
    id: 'gen-4',
    question: '¿En qué idioma está?',
    answer:
      'La aplicación está en español. Para trabajar con otros mercados, podés cargar varias divisas, tipos de cambio y países en Datos Maestros.',
    category: 'general',
  },
  {
    id: 'gen-5',
    question: '¿Puedo usar Prixia desde el celular?',
    answer:
      'Sí, desde el navegador del celular o de la tablet: las pantallas se acomodan al tamaño. Para armar cotizaciones largas o revisar tablas grandes, conviene una computadora.',
    category: 'general',
  },
  {
    id: 'gen-6',
    question: '¿Qué tan segura es la plataforma?',
    answer:
      'Las contraseñas se guardan cifradas, la sesión usa tokens de acceso y de renovación y se cierra sola después de un rato sin uso, cada empresa tiene sus datos aislados y cada persona ve sólo lo que su rol le permite.',
    category: 'general',
  },
  {
    id: 'gen-7',
    question: '¿Sirve para trabajar en Bolivia?',
    answer:
      'Sí. Prixia maneja NIT, bolivianos y tipos de cambio, partidas arancelarias en los materiales, y en Comercia › Asistente arancelario podés buscar una partida del arancel boliviano y liquidar los tributos de una importación (gravamen, IVA, ICE e IEHD).',
    category: 'general',
  },

  // ── Modelos de precio ──────────────────────────────────────────
  {
    id: 'pri-1',
    question: '¿Cómo funcionan los modelos de precio?',
    answer:
      'Un modelo dice cómo se calcula el precio de una cotización: qué datos se cargan (las variables) y qué fórmulas se aplican. Se crean en Cotiza › Modelos con «Nuevo modelo», a partir de una plantilla, y en cada cotización elegís qué modelo usa cada sección.',
    category: 'pricing',
  },
  {
    id: 'pri-2',
    question: '¿Qué tipos de variables existen?',
    answer:
      'Nueve: Número, Porcentaje, Moneda, Texto, Booleano (sí o no), Calculada (con fórmula), Enum (una lista para elegir), Escalonada (precios por volumen) y Lookup (una tabla de dos dimensiones). Se agregan en la ficha del modelo con «Nueva Variable».',
    category: 'pricing',
  },
  {
    id: 'pri-3',
    question: '¿Cómo se escriben las fórmulas?',
    answer:
      'Una variable calculada usa los códigos de otras variables, por ejemplo CANTIDAD * COSTO_UNITARIO. Prixia revisa la fórmula mientras escribís, te dice si es válida o qué está mal, y te muestra las variables que podés usar. «Ver Grafo», en la ficha del modelo, muestra qué variable depende de cuál.',
    category: 'pricing',
  },
  {
    id: 'pri-4',
    question: '¿Qué son las tablas de Lookup?',
    answer:
      'Matrices de dos dimensiones para buscar un valor. Por ejemplo, las filas son países, las columnas son niveles de servicio y cada celda tiene la tasa. Se crean en la ficha del modelo, en «Tablas de Lookup», con «Nueva Tabla».',
    category: 'pricing',
  },
  {
    id: 'pri-5',
    question: '¿Cómo funcionan los precios por volumen?',
    answer:
      'Con una variable Escalonada. Cargás niveles con cantidad mínima, cantidad máxima, precio unitario y descuento (por ejemplo, de 1 a 10 unidades un precio, de 11 a 50 otro). Prixia usa el nivel que corresponde a la cantidad de cada línea.',
    category: 'pricing',
  },
  {
    id: 'pri-6',
    question: '¿Puedo usar varios modelos en una misma cotización?',
    answer:
      'Sí. Una cotización puede tener varias secciones y cada una usa su propio modelo: por ejemplo, licencias en una y hardware en otra. En Nueva cotización, «Agregar Sección (Pricer)» suma una sección.',
    category: 'pricing',
  },
  {
    id: 'pri-7',
    question: '¿Qué diferencia hay entre margen objetivo y margen piso?',
    answer:
      'El objetivo es el margen que buscás; el piso, el mínimo aceptable. En la lista de cotizaciones el margen se pinta verde si llega al objetivo, ámbar si queda entre el piso y el objetivo, y rojo si queda debajo del piso. Cada línea de negocio puede tener su margen objetivo y su margen piso en Datos Maestros › Líneas de negocio.',
    category: 'pricing',
  },
  {
    id: 'pri-8',
    question: '¿Qué diferencia hay entre variables globales y por línea?',
    answer:
      'Si marcás «Variable Global», el valor se comparte en toda la sección de la cotización (por ejemplo, el tipo de cambio o el IVA). Si no, cada línea tiene su propio valor (por ejemplo, la cantidad o el costo unitario).',
    category: 'pricing',
  },

  // ── Cotizaciones ───────────────────────────────────────────────
  {
    id: 'cot-1',
    question: '¿Cómo creo una cotización?',
    answer:
      'En Cotiza › Nueva cotización, en un solo formulario: completás cliente, email, país, línea de negocio, divisa y validez; en cada sección elegís el modelo, completás las variables globales y agregás los ítems; y al final tocás «Crear Cotización». Si falta algo, Prixia te dice qué. «Guardar Borrador» la guarda sin terminar.',
    category: 'cotizaciones',
  },
  {
    id: 'cot-2',
    question: '¿Qué es una cotización con varias secciones?',
    answer:
      'Una cotización que combina varios modelos de precio en un solo documento. Cada sección tiene su modelo, sus variables y sus ítems, y abajo ves el gran total de todas. Sirve cuando una propuesta junta cosas distintas, como software, hardware y soporte.',
    category: 'cotizaciones',
  },
  {
    id: 'cot-3',
    question: '¿Puedo bajar la cotización en PDF?',
    answer: 'Sí. En la ficha de la cotización, «Descargar PDF».',
    category: 'cotizaciones',
  },
  {
    id: 'cot-4',
    question: '¿Puedo reusar una cotización como base para otra?',
    answer:
      'Sí, con plantillas. En la ficha de la cotización, «Guardar como Plantilla» le pone un nombre y la guarda. Después, en la lista de cotizaciones, la pestaña Plantillas tiene «Usar Plantilla» para crear una cotización nueva a partir de ella.',
    category: 'cotizaciones',
  },
  {
    id: 'cot-5',
    question: '¿Qué estados puede tener una cotización?',
    answer:
      'Borrador, En aprobación, Aprobada, Ajuste solicitado, Rechazada, Vencida (pasó su plazo de validez), Cancelada y Congelada.',
    category: 'cotizaciones',
  },
  {
    id: 'cot-6',
    question: '¿En qué monedas puedo cotizar?',
    answer:
      'En cualquier divisa cargada en Datos Maestros › Divisas. Si un cálculo necesita un tipo de cambio que todavía no está cargado, Prixia te avisa; se registra en Datos Maestros › Tipos de cambio.',
    category: 'cotizaciones',
  },
  {
    id: 'cot-7',
    question: '¿Cómo le hago llegar la cotización al cliente?',
    answer:
      'Dos formas: bajás el PDF con «Descargar PDF» y lo mandás vos, o tocás «Compartir con cliente», que genera un link público para que tu cliente vea la cotización (monto y detalle) sin necesidad de cuenta. Ese link se puede revocar.',
    category: 'cotizaciones',
  },
  {
    id: 'cot-8',
    question: '¿Queda registro de quién aprobó o rechazó una cotización?',
    answer:
      'Sí. En Cotiza › Aprobaciones, el «Historial» muestra cada decisión: la cotización, el estado, quién la decidió, su comentario y la fecha. Se puede filtrar por tipo: Aprobación, Reapertura o Automáticas.',
    category: 'cotizaciones',
  },

  // ── Aprobaciones ───────────────────────────────────────────────
  {
    id: 'apr-1',
    question: '¿Cómo funciona el flujo de aprobaciones?',
    answer:
      'Al crear una cotización, Prixia aplica las reglas de aprobación de tu empresa: si no hace falta aprobación, queda aprobada; si hace falta, pasa a En aprobación y le llega al aprobador. En Cotiza › Aprobaciones, el aprobador puede aprobar (comentario opcional), rechazar (motivo obligatorio) o solicitar un ajuste (precio sugerido y comentario obligatorios).',
    category: 'aprobaciones',
  },
  {
    id: 'apr-2',
    question: '¿Quién puede aprobar cotizaciones?',
    answer:
      'Los aprobadores y los administradores de la empresa. Quién firma cada caso lo define el administrador en Mi empresa › Aprobaciones, por cargo o por persona. Si una cotización es tuya y no te corresponde firmarla, en lugar de los botones aparece «Cotización propia».',
    category: 'aprobaciones',
  },
  {
    id: 'apr-3',
    question: '¿Qué significa «Ajuste solicitado»?',
    answer:
      'Que el aprobador pidió cambiar el precio. El vendedor ve en la cotización el aviso «Ajuste Solicitado por Aprobador», con el comentario y el precio sugerido. Toca «Editar», corrige y después «Reenviar», y la cotización vuelve a la cola del aprobador.',
    category: 'aprobaciones',
  },
  {
    id: 'apr-4',
    question: '¿Me avisan cuando cambia una aprobación?',
    answer:
      'Sí, con las notificaciones de la barra de arriba. El aprobador recibe un aviso cuando le llega una cotización; el vendedor, cuando su cotización se aprueba, se rechaza o le piden un ajuste.',
    category: 'aprobaciones',
  },
  {
    id: 'apr-5',
    question: '¿Una cotización se puede aprobar sola?',
    answer:
      'Sí. En Mi empresa › Aprobaciones, «¿Cuándo se pide aprobación?» puede ser «Sólo si no cumple el margen» o «Siempre». Si una cotización no necesita aprobación, queda aprobada al crearla, y en el Historial de Aprobaciones figura como «Aprobada automáticamente».',
    category: 'aprobaciones',
  },
  {
    id: 'apr-6',
    question: '¿Puedo pedir aprobación según el monto u otras condiciones?',
    answer:
      'Sí. En Mi empresa › Aprobaciones, «Agregar regla» define cuándo se activa una regla y quién firma. Las condiciones pueden ser el monto, el margen, la línea de negocio, el segmento del cliente, el modelo de precios o que supere el presupuesto del cliente. Cada regla dice cuántas firmas necesita.',
    category: 'aprobaciones',
  },

  // ── Datos Maestros ─────────────────────────────────────────────
  {
    id: 'dm-1',
    question: '¿Qué son los Datos Maestros?',
    answer:
      'Los catálogos que usa todo el sistema: Materiales, Divisas, Tipos de cambio, Líneas de negocio, Países, Tipos de cotización, Centros de beneficio e Incoterms, y los de flete: Tramos y tarifas, Proveedores de flete, Servicios de flete y Ubicaciones. Están en el menú, bajo Datos y cuenta.',
    category: 'datos-maestros',
  },
  {
    id: 'dm-2',
    question: '¿Cómo configuro las divisas y los tipos de cambio?',
    answer:
      'En Datos Maestros › Divisas cargás cada moneda con su código ISO de 3 letras, su símbolo, su nombre y su tasa contra la divisa base (la base siempre vale 1). En Tipos de cambio registrás el tipo de cambio de cada par de monedas por fecha, con «Nuevo TC». USDT y USDC se actualizan solas cada 30 minutos; «Sincronizar TC» fuerza la actualización.',
    category: 'datos-maestros',
  },
  {
    id: 'dm-3',
    question: '¿Cómo funcionan las líneas de negocio?',
    answer:
      'Tienen tres niveles: Sector, Vertical y Marca. Con «+ Vertical» y «+ Marca» agregás un nivel dentro de otro. Cada línea puede tener su margen objetivo y su margen piso.',
    category: 'datos-maestros',
  },
  {
    id: 'dm-4',
    question: '¿Dónde se cargan los aranceles?',
    answer:
      'Cada material tiene su partida arancelaria y su gravamen arancelario, con un historial de tasas. Para ver qué paga una partida y liquidar una importación completa, usá Comercia › Asistente arancelario.',
    category: 'datos-maestros',
  },
  {
    id: 'dm-5',
    question: '¿Cómo cargo materiales?',
    answer:
      'En Datos Maestros › Materiales, con «Nuevo Material»: código, tipo (Producto o Servicio), nombre, línea de negocio, partida arancelaria y gravamen. Los productos suman SKU, marca, modelo, unidad de medida, peso y medidas. Al editar un material cargás su precio en cada lista de precios.',
    category: 'datos-maestros',
  },
  {
    id: 'dm-6',
    question: '¿Puedo cargar datos maestros de forma masiva?',
    answer:
      'Sí. Los administradores tienen «Importar CSV» en cada pestaña de Datos Maestros: bajás la plantilla, la completás y la subís. Al terminar, Prixia te dice cuántos registros se crearon, cuántos se actualizaron y qué filas fallaron.',
    category: 'datos-maestros',
  },

  // ── Inteligencia artificial ────────────────────────────────────
  {
    id: 'ia-1',
    question: '¿Dónde está el Asistente IA?',
    answer:
      'El asistente flotante ya no está en Prixia. Para dudas de uso, tenés este Centro de Ayuda (manual y preguntas frecuentes), el Curso Interactivo y el Tutorial, en el grupo Ayuda del menú.',
    category: 'ia',
  },
  {
    id: 'ia-2',
    question: '¿Qué herramientas con IA hay?',
    answer:
      'En Cotiza › Inteligencia de Pricing hay herramientas de análisis con IA para afinar tu estrategia de precios, en cuatro pestañas: Simulador, Predicciones, Recomendaciones e Insights.',
    category: 'ia',
  },

  // ── Cuenta ─────────────────────────────────────────────────────
  {
    id: 'cta-1',
    question: '¿Cómo registro mi empresa?',
    answer:
      'En la página de registro, en cinco pasos: el tipo de negocio, el país, cuántas personas trabajan (opcional), los datos de la empresa y tu cuenta de acceso. Se crea la empresa y vos quedás como administrador. Si el registro no está habilitado, la página te pide que le pidas acceso a tu administrador.',
    category: 'cuenta',
  },
  {
    id: 'cta-2',
    question: '¿Cómo recupero mi contraseña?',
    answer:
      'En la página de inicio de sesión, tocá «¿Olvidaste tu contraseña?» y escribí tu correo. Te llega un enlace para crear una contraseña nueva. Si no aparece en unos minutos, revisá la carpeta de spam o pedí otro.',
    category: 'cuenta',
  },
  {
    id: 'cta-3',
    question: '¿Cómo agrego usuarios a mi empresa?',
    answer:
      'Como administrador, en Mi empresa › Usuarios. «Nuevo usuario» lo crea con nombre, apellido, correo, contraseña y rol. También podés crear un link de invitación con «Crear link»: elegís el rol y el dominio permitido, y sólo los correos de ese dominio pueden registrarse con ese link.',
    category: 'cuenta',
  },
  {
    id: 'cta-4',
    question: '¿Qué roles existen?',
    answer:
      'Los roles base son Administrador de empresa (acceso total dentro de su empresa), Vendedor (crea y edita sus cotizaciones), Aprobador (revisa y decide las cotizaciones) y Administrador de plataforma (Datec, para soporte). En Mi empresa › Roles el administrador puede crear roles propios y elegir, módulo por módulo, si cada uno está Habilitado, Bloqueado u Oculto.',
    category: 'cuenta',
  },
  {
    id: 'cta-5',
    question: '¿Cómo actualizo mi perfil o los datos de la empresa?',
    answer:
      'Tus datos y tu contraseña, en «Mi Perfil», desde el menú de tu cuenta (abajo, en el menú lateral). Los datos de la empresa (nombre, NIT, dirección, logo y color de la marca) están en Mi empresa › Configuración, para administradores.',
    category: 'cuenta',
  },

  // ── Compite: productos, competencia y monitoreo ───────────────
  {
    id: 'pd-1',
    question: '¿Para qué sirve Compite?',
    answer:
      'Para comparar tus precios con los de la competencia y recibir precios nuevos para decidir. Se usa en este orden: cargás tus productos, revisás qué tiendas comparás, vinculás tus productos con los de cada tienda, ponés reglas, creás estrategias y aprobás o descartás cada precio. Nada cambia solo, salvo que le des permiso a una estrategia para aplicar sus precios.',
    category: 'pricing-dinamico',
  },
  {
    id: 'pd-2',
    question: '¿Dónde quedaron Pricing Dinámico y Precio Inteligente?',
    answer:
      'Están repartidos en las pantallas de Compite: Decisiones, Monitoreo y Análisis de mercado para el día a día; Estrategia, Productos y Competencia para la configuración. Si tenés guardada una dirección vieja, te lleva sola a la pantalla nueva.',
    category: 'pricing-dinamico',
  },
  {
    id: 'pd-3',
    question: '¿Cómo cargo mis productos?',
    answer:
      'En Compite › Productos › Mis productos (administradores): «Nuevo producto» para cargar uno a mano, o «Importar CSV» para muchos de una vez con la plantilla de Excel. Sólo el código, el nombre y el precio son obligatorios, pero sin costo ninguna regla de margen puede cuidar el producto. «Subir fotos» carga las fotos en un ZIP, cada una con el SKU como nombre.',
    category: 'pricing-dinamico',
  },
  {
    id: 'pd-4',
    question: '¿Cómo agrego una tienda de la competencia?',
    answer:
      'Las tiendas con sitio web las lee Prixia para todas las empresas: te las habilita la plataforma y aparecen en Compite › Competencia, donde elegís si entran en tu análisis. Si te falta una, pedísela a tu contacto de Prixia. «Agregar tienda» sirve para una tienda sin sitio web (por ejemplo, un proveedor), cuyos precios entran a mano.',
    category: 'pricing-dinamico',
  },
  {
    id: 'pd-5',
    question: '¿Cómo vinculo mis productos con los de la competencia?',
    answer:
      'En Compite › Productos › Vincular, elegís el competidor y tocás «Ejecutar inferencia»: Prixia propone candidatos y vos confirmás cada vínculo o lo descartás. Nada se vincula solo. Con más de un competidor leído, «Todas las tiendas» los compara uno tras otro.',
    category: 'pricing-dinamico',
  },
  {
    id: 'pd-6',
    question: '¿Dónde comparo mis precios con un competidor?',
    answer:
      'En Compite › Monitoreo, pestaña Productos. Elegí la tienda en «Comparar contra» y, en «Orden», ver primero donde sos más caro o donde sos más barato. Tocá un producto para ver su panel: tu precio, tu costo, tu margen y cuánto más caro o más barato estás que el más barato.',
    category: 'pricing-dinamico',
  },
  {
    id: 'pd-7',
    question: '¿Cómo me entero de que la competencia cambió un precio?',
    answer:
      'En Compite › Monitoreo, pestaña Cambios: arriba ves los cambios de las últimas horas y las lecturas fallidas, y en «Avisos» te llega un aviso cuando una tienda cambia el precio de un producto tuyo, cuando una lectura falla o cuando aparecen productos nuevos. Los administradores fijan, en «Ajustes», desde qué cambio de precio llega un aviso.',
    category: 'pricing-dinamico',
  },
  {
    id: 'pd-8',
    question: '¿Puedo mandar los precios a mi tienda online?',
    answer:
      'Sí. En Compite › Productos, «Aplicar a tienda» (administradores) abre «Enviar precios a tienda»: elegís la tienda, revisás cada cambio y tocás «Aplicar cambios». Antes tenés que conectar la tienda en Integraciones › Tiendas conectadas. Cada envío queda en Integraciones › Historial de envíos, desde donde se puede revertir.',
    category: 'pricing-dinamico',
  },
  {
    id: 'pd-9',
    question: '¿Qué hago si una tienda no se lee bien?',
    answer:
      'En Compite › Competencia, abrí «Lecturas programadas»: ahí ves cuándo se lee cada tienda, qué se leyó y qué falló, y podés volver a leerla a mano con «Chequear precios» o «Leer el catálogo completo». En Monitoreo › Cambios, el filtro «Con error» muestra las lecturas que fallaron.',
    category: 'pricing-dinamico',
  },

  // ── Compite: estrategias y decisiones ──────────────────────────
  {
    id: 'sp-1',
    question: '¿Qué es una estrategia?',
    answer:
      'Una estrategia elige productos (por categoría y marca, o productos puntuales), calcula su precio con un método y te lo propone para que decidas. Se crean en Compite › Estrategia › Estrategias, con «Crear una estrategia», en cuatro pasos: A quién, Cómo calcula, Cuándo y con qué permiso, y Resultados esperados.',
    category: 'smart-pricing',
  },
  {
    id: 'sp-2',
    question: '¿Qué métodos puede usar una estrategia?',
    answer:
      'Cinco: Igualar al más barato; Quedar por debajo del más barato (por el % que digas); Apuntar a un margen; Precio inteligente (mira el mercado, la demanda y el stock a la vez); y Bajar un % de mi precio de lista, que no necesita costo ni competencia y sirve desde el primer día.',
    category: 'smart-pricing',
  },
  {
    id: 'sp-3',
    question: '¿Cuándo cambia el precio real de un producto?',
    answer:
      'Cuando aprobás la propuesta en Compite › Decisiones › Revisar precios, de a una o en lote. La excepción es una estrategia con permiso para aplicar sola: «Aplica sola» o «Aplica sola, pero preguntame si el cambio supera un %». Lo que se aplicó solo queda en la pestaña Aplicadas solas. «Aplicar las reglas y recalcular», en Reglas, también manda precios nuevos a Revisar precios.',
    category: 'smart-pricing',
  },
  {
    id: 'sp-4',
    question: '¿Qué diferencia hay entre Decisiones y Revisar precios?',
    answer:
      'Decisiones es la portada del día: cuántos precios esperan tu sí, cuánta plata hay en juego y qué propone cada estrategia. Ahí no se decide: «Revisar los…» o una fila de «Por estrategia» te lleva a Revisar precios, donde aprobás o descartás cada precio con todo su contexto.',
    category: 'smart-pricing',
  },
  {
    id: 'sp-5',
    question: '¿Por qué algunos precios no se pueden aprobar en lote?',
    answer:
      'Porque se deciden de a uno: los que tienen un conflicto entre reglas (se resuelven con «Decidir»), y los que quedan debajo del costo, se calcularon hace días y el mercado cambió, o usaron una tienda que ya no está entre las tuyas (se aprueban con «Aprobar esta»). Si el precio o el costo cambiaron después del cálculo, la propuesta queda trabada.',
    category: 'smart-pricing',
  },
  {
    id: 'sp-6',
    question: '¿Puedo deshacer un precio que ya se aprobó?',
    answer:
      'Sí, si sos administrador. En Revisar precios, en las pestañas Aprobadas y Aplicadas solas, tenés «Volver al precio de antes». También desde el historial de precios del producto (en su ficha, «Historial») podés volver a un precio anterior. Y si descartaste algo sin querer, el aviso del descarte te deja «Deshacer».',
    category: 'smart-pricing',
  },
  {
    id: 'sp-7',
    question: '¿Por qué un producto no tiene precio propuesto?',
    answer:
      'Puede no tener estrategia que lo alcance, no tener costo cargado (y su método lo necesita) o no tener precios de competencia leídos (e igualar o quedar por debajo del más barato los necesita). En Compite › Estrategia › Reglas, «Cómo queda cada producto» muestra cómo está cada uno: dentro del rango, limitado por una regla, con conflicto entre reglas, sin costo, sin estrategia o sin poder calcularse.',
    category: 'smart-pricing',
  },
  {
    id: 'sp-8',
    question: '¿Qué pasa si apago o doy de baja una estrategia?',
    answer:
      'Apagada, deja de correr hasta que la enciendas, y lo que tenía esperando en Revisar precios se retira (pasa a Descartadas). Dada de baja, deja de correr y de proponer, pero las propuestas que ya generó no se borran: quedan en el historial con su nombre.',
    category: 'smart-pricing',
  },

  // ── Descuentos ─────────────────────────────────────────────────
  {
    id: 'desc-1',
    question: '¿Dónde quedaron los Descuentos?',
    answer:
      'La pantalla de Descuentos ya no está en Prixia: la dirección vieja te lleva a Compite. Para mover precios, usá las estrategias y las reglas de Compite. En Integraciones › Historial de envíos, el filtro de tipo «Descuento» muestra los envíos de descuentos que se hayan hecho antes.',
    category: 'smart-pricing',
  },

  // ── Reglas ─────────────────────────────────────────────────────
  {
    id: 'rp-1',
    question: '¿Qué es una regla?',
    answer:
      'Un límite que ningún precio puede pasar, lo proponga la estrategia que lo proponga. Por ejemplo: «No ganar menos de 12 %» en todo el catálogo. La regla nunca decide el precio: eso lo hace la estrategia; la regla sólo dice hasta dónde puede llegar. Están en Compite › Estrategia › Reglas.',
    category: 'reglas-pricing',
  },
  {
    id: 'rp-2',
    question: '¿Qué límites puedo poner?',
    answer:
      'Siete: No ganar menos de… (margen mínimo sobre el precio de venta); No mover más de… por vez (con subida y bajada máxima por separado); No quedar más de… arriba del más barato; No quedar más de… debajo del más barato; No vender por debajo de Bs… y No vender por encima de Bs… (sólo para un producto); y Terminar en… (el redondeo, que va al final).',
    category: 'reglas-pricing',
  },
  {
    id: 'rp-3',
    question: '¿A quién alcanza una regla?',
    answer:
      'A todo el catálogo, por categoría y marca (si elegís las dos, tienen que cumplirse las dos: Televisores + Sony son sólo los televisores Sony) o a productos puntuales. Podés sacar productos con «Excepciones». Mientras la armás, la pantalla dice a cuántos productos alcanza.',
    category: 'reglas-pricing',
  },
  {
    id: 'rp-4',
    question: '¿Qué pasa si dos reglas alcanzan al mismo producto?',
    answer:
      'Si son del mismo tipo, rige la más específica: una de este producto le gana a una de su marca o categoría, y ésa a una de todo el catálogo, aunque pida menos. Si una regla de todo el catálogo pide ganar al menos 18 % y otra de Sony pide 12 %, en los productos Sony queda 12 % y en el resto 18 %. Si son de tipos distintos (un margen y un tope de movimiento, por ejemplo), se cumplen todas y decide la más exigente.',
    category: 'reglas-pricing',
  },
  {
    id: 'rp-5',
    question: '¿Qué es un conflicto entre reglas?',
    answer:
      'Cuando las reglas que alcanzan a un producto no se pueden cumplir todas a la vez (por ejemplo, subió el costo y el margen mínimo pide un precio que la regla de movimiento no deja alcanzar). El precio no se mueve solo: te espera en Revisar precios, donde lo resolvés con «Decidir».',
    category: 'reglas-pricing',
  },
  {
    id: 'rp-6',
    question: '¿Puedo ver qué haría una regla antes de que rija?',
    answer:
      'Sí. El paso 3 de «Nueva regla», Qué cambiaría, muestra a cuántos productos alcanza, a cuántos les cambia el precio, cuántos quedarían con un conflicto entre reglas y cuántos no tienen costo. Además, una regla nueva o cambiada no rige hasta que tocás «Aplicar las reglas y recalcular», que antes te muestra producto por producto el precio de hoy y el que quedaría.',
    category: 'reglas-pricing',
  },

  // ── Clientes ───────────────────────────────────────────────────
  {
    id: 'cli-1',
    question: '¿Cómo agrego un cliente?',
    answer:
      'En Cotiza › Gestión › Clientes, con «Nuevo cliente». Nombre y código son obligatorios; además podés cargar segmento, industria, email, teléfono, NIT, sitio web, dirección y notas. Tocá «Crear Cliente». También podés crearlo desde Nueva cotización, con «Nuevo Cliente».',
    category: 'clientes',
  },
  {
    id: 'cli-2',
    question: '¿Puedo importar clientes de forma masiva?',
    answer:
      'Sí. En Clientes, «Importar CSV»: bajás la plantilla, la completás y la subís. Prixia crea los clientes nuevos y actualiza los que ya existían.',
    category: 'clientes',
  },
  {
    id: 'cli-3',
    question: '¿Cómo manejo los contactos de un cliente?',
    answer:
      'En la tabla de clientes, tocá la flecha de la fila para ver sus contactos. «Agregar Contacto» pide nombre, apellido y email (obligatorios), teléfono y cargo. Marcá «Contacto principal» si es la persona de referencia.',
    category: 'clientes',
  },
  {
    id: 'cli-4',
    question: '¿Para qué sirven los segmentos de cliente?',
    answer:
      'Para clasificar a tus clientes en Enterprise, PyME, Gobierno o Startup. El segmento también puede ser una condición de las reglas de aprobación, en Mi empresa › Aprobaciones.',
    category: 'clientes',
  },
  {
    id: 'cli-5',
    question: '¿Puedo ver cuántas cotizaciones tiene cada cliente?',
    answer:
      'Sí. En la tabla de clientes, la columna «Cotizaciones» muestra cuántas tiene cada uno.',
    category: 'clientes',
  },
  {
    id: 'cli-6',
    question: '¿Puedo buscar clientes por NIT?',
    answer:
      'Sí. El buscador de Clientes busca por nombre, código, NIT o email. Además podés filtrar por origen: los cargados en Prixia o los que vienen de HubSpot.',
    category: 'clientes',
  },

  // ── Comercia ───────────────────────────────────────────────────
  {
    id: 'imp-1',
    question: '¿Qué es Comercia?',
    answer:
      'El módulo que calcula cuánto te cuesta realmente importar, del precio del proveedor al producto puesto en tu almacén. Tiene Cotizador y Cotizaciones (para cotizar fletes), Import / Export y Contenedores (para operar los embarques), y Tarifario, Indicadores y Asistente arancelario. Antes se llamaba Importaciones.',
    category: 'importaciones',
  },
  {
    id: 'imp-2',
    question: '¿Qué es el costo nacionalizado (landed cost)?',
    answer:
      'El costo real de tu mercadería puesta en destino: el valor FOB más flete, seguro, aranceles, impuestos y otros gastos. Se calcula en cada orden, y la pestaña Costos de su ficha muestra de dónde sale cada número.',
    category: 'importaciones',
  },
  {
    id: 'imp-3',
    question: '¿Qué diferencia hay entre el Cotizador y una orden?',
    answer:
      'El Cotizador calcula el flete del embarque: compara rutas y transportistas con el tarifario vigente y genera una cotización. El costo de cada producto puesto en destino se calcula en la orden, con sus productos.',
    category: 'importaciones',
  },
  {
    id: 'imp-4',
    question: '¿Cómo paso una cotización de Comercia a orden?',
    answer:
      'En Comercia › Cotizaciones, con «Convertir» en la fila, o en la ficha de la cotización con «Convertir a orden». La orden queda como Borrador; después la confirmás con «Confirmar Orden» y la seguís en Import / Export.',
    category: 'importaciones',
  },
  {
    id: 'imp-5',
    question: '¿Cómo veo mis embarques por estado?',
    answer:
      'En Comercia › Import / Export, la vista Pipeline (la que se abre primero) muestra una columna por estado, de Cotización a Entregada, y avisa qué llega hoy, mañana o con retraso. También hay una vista Lista, y «Filtros» por estado, proveedor y fechas.',
    category: 'importaciones',
  },
  {
    id: 'imp-6',
    question: '¿Por qué no puedo iniciar un tramo?',
    answer:
      'Porque los tramos se mueven sólo con la orden confirmada y en orden: cada uno espera a que se complete el anterior. Con la orden confirmada, «Iniciar» lo pasa a En curso y «Completar» a Completado, guardando las fechas reales. En una orden cancelada o entregada, el recorrido queda de sólo lectura.',
    category: 'importaciones',
  },
  {
    id: 'imp-7',
    question: '¿Cómo hago el seguimiento de un envío?',
    answer:
      'En la ficha de la orden, pestaña Seguimiento, con «Registrar Evento»: elegís estado, título, fecha, ubicación, descripción y, si querés, a qué tramo pertenece. La pestaña se habilita cuando la carga zarpa. Con «Compartir con cliente», tu cliente puede seguir el embarque con un link.',
    category: 'importaciones',
  },
  {
    id: 'imp-8',
    question: '¿Qué documentos puedo subir a una orden?',
    answer:
      'En la pestaña Documentos, con «Subir Documento»: conocimiento de embarque (BL), factura comercial, lista de empaque, certificado de origen, certificado fitosanitario, declaración aduanera, póliza de seguro u otro. Se habilita cuando la carga zarpa.',
    category: 'importaciones',
  },
  {
    id: 'imp-9',
    question: '¿Cómo cargo el tarifario?',
    answer:
      'En Comercia › Tarifario, en «Cargar el tarifario de la semana»: arrastrá el Excel o tocá «Elegir archivo» («Descargar plantilla» baja el formato). Prixia te muestra un reporte con lo que corrigió, las filas rechazadas y los desgloses que no cuadran, y lo confirmás con «Confirmar importación». El número junto a Tarifario, en el menú, son las tarifas vencidas.',
    category: 'importaciones',
  },
  {
    id: 'imp-10',
    question: '¿Cómo sé qué tributos paga mi producto?',
    answer:
      'En Comercia › Asistente arancelario: buscá la mercadería o el código de la partida y abrí su ficha. En la pestaña Tributos ves el gravamen, el IVA, el ICE y el IEHD, y con «Liquidar» calculás el CIF, cada tributo y el costo nacionalizado a partir del valor FOB, el flete y el seguro. Si la mercadería no se puede importar, lo ves antes de las pestañas.',
    category: 'importaciones',
  },
  {
    id: 'imp-11',
    question: '¿Qué muestra Indicadores?',
    answer:
      'Comercia › Indicadores muestra las órdenes por estado, los días promedio en tránsito y en aduana, la conversión de cotizaciones, los proveedores con más volumen, la tendencia por mes y las órdenes con sobrecosto alto sobre el FOB.',
    category: 'importaciones',
  },

  // ── Integraciones ──────────────────────────────────────────────
  {
    id: 'int-1',
    question: '¿Cómo conecto mi tienda online?',
    answer:
      'En Integraciones › Tiendas conectadas, con «Conectar tienda»: completás el nombre, la URL de la tienda y la clave (Store Secret / API Key) que te da tu plataforma de comercio electrónico. Usá «Probar conexión» antes de mandar precios.',
    category: 'integraciones',
  },
  {
    id: 'int-2',
    question: '¿Cómo creo una clave API?',
    answer:
      'En Integraciones › Claves API, con «Crear API Key»: le ponés un nombre que la identifique y se genera la clave. La clave completa se muestra una sola vez: copiala y guardala en un lugar seguro. Si la perdés, creá otra. «Revocar» anula una clave en el momento.',
    category: 'integraciones',
  },
  {
    id: 'int-3',
    question: '¿Puedo probar la API antes de integrar?',
    answer:
      'Sí. En Integraciones › Claves API, la tarjeta «Probar API»: pegás la clave, elegís el tipo de dato (Productos o Inventario), escribís el cuerpo en JSON y tocás «Enviar». El probador de productos sólo revisa y no carga nada; el de inventario actualiza de verdad el stock de los SKU que mandes.',
    category: 'integraciones',
  },
  {
    id: 'int-4',
    question: '¿Puedo revertir un envío de precios a mi tienda?',
    answer:
      'Sí. En Integraciones › Historial de envíos, los envíos con estado Aplicado tienen «Revertir», que devuelve los precios anteriores a la tienda.',
    category: 'integraciones',
  },
  {
    id: 'int-5',
    question: '¿Cuántas peticiones puedo hacer a la API?',
    answer:
      'Depende del plan: Trial, 30 por minuto y 1.000 por día; Basic, 100 por minuto y 10.000 por día; Professional, 500 por minuto y 50.000 por día; Enterprise, 2.000 por minuto y 200.000 por día. Están en Integraciones › Integración API.',
    category: 'integraciones',
  },
  {
    id: 'int-6',
    question: '¿Qué datos puedo mandar por la API?',
    answer:
      'Productos (el catálogo) e inventario (el stock). Para consultar precios están GET /api/v1/price/:sku y POST /api/v1/prices (hasta 100 SKU por llamada). Integraciones › Integración API tiene todos los endpoints y ejemplos en JavaScript, Python, PHP y cURL.',
    category: 'integraciones',
  },
  {
    id: 'int-7',
    question: '¿Cómo conecto SAP o HubSpot?',
    answer:
      'En Integraciones › SAP y CRM, con «Nueva integración»: elegís SAP S/4HANA (Materiales), SAP C4C (CRM / Oportunidades) o HubSpot CRM y cargás los datos de conexión. Cada integración tiene «Probar», «Importar» y «Exportar» (HubSpot no tiene «Exportar»), y abajo queda el historial de sincronización.',
    category: 'integraciones',
  },

  // ── Técnico ────────────────────────────────────────────────────
  {
    id: 'tec-1',
    question: '¿Prixia tiene una API?',
    answer:
      'Sí. Para conectar tu tienda o tu ERP, la API de precios usa una clave (Integraciones › Claves API) y está documentada en Integraciones › Integración API, con un enlace a la documentación interactiva (Swagger UI). Para programadores, Mi empresa › API Developers documenta además las API internas de materiales, cotizaciones y modelos.',
    category: 'tecnico',
  },
  {
    id: 'tec-2',
    question: '¿Con qué sistemas se puede integrar?',
    answer:
      'Con SAP S/4HANA, SAP C4C y HubSpot CRM desde Integraciones › SAP y CRM, con tiendas online desde Integraciones › Tiendas conectadas, y con cualquier sistema que use la API de precios.',
    category: 'tecnico',
  },
  {
    id: 'tec-3',
    question: '¿Puedo exportar mis datos?',
    answer:
      'Sí, en varias pantallas: las cotizaciones en PDF («Descargar PDF»), y la lista de productos y la comparación con la competencia con «Exportar», en Compite › Productos y en Compite › Monitoreo. Para integraciones, la API.',
    category: 'tecnico',
  },
];
